import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface NavigationLink {
  path: string;
  label: string;
  visible: boolean;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  category: string;
  created_at: string;
  text_direction: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  level: string;
  price: string;
  status: string;
  image_url: string | null;
}

interface Testimonial {
  id: number;
  name: string;
  content: string;
  location: string;
  image: string | null;
  display_order: number;
  created_at: string;
}

interface DataContextType {
  navigationLinks: NavigationLink[];
  blogPosts: BlogPost[];
  courses: Course[];
  testimonials: Testimonial[];
  isLoading: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [navigationLinks, setNavigationLinks] = useState<NavigationLink[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all data in parallel to minimize request chains
        const [navResponse, blogResponse, courseResponse, testimonialResponse] = await Promise.all([
          supabase
            .from('navigation_links')
            .select('path, label, visible')
            .order('order', { ascending: true }),
          supabase
            .from('blog_posts')
            .select('id, title, excerpt, image_url, category, created_at, text_direction')
            .eq('published', true)
            .order('created_at', { ascending: false })
            .limit(3),
          supabase
            .from('courses')
            .select('id, title, description, level, price, status, image_url')
            .order('created_at', { ascending: false })
            .limit(3),
          supabase
            .from('testimonials')
            .select('*')
            .order('display_order', { ascending: true })
        ]);

        // Handle responses
        if (!navResponse.error && navResponse.data) {
          setNavigationLinks(navResponse.data);
        }
        
        if (!blogResponse.error && blogResponse.data) {
          setBlogPosts(blogResponse.data);
        }
        
        if (!courseResponse.error && courseResponse.data) {
          setCourses(courseResponse.data);
        }
        
        if (!testimonialResponse.error && testimonialResponse.data) {
          setTestimonials(testimonialResponse.data);
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const value: DataContextType = {
    navigationLinks,
    blogPosts,
    courses,
    testimonials,
    isLoading,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};